import { FC, useEffect, useRef } from 'react';

import { KeyboardCode } from '@telialt/common-constants/src/keyboard-codes';

import { MIN_SEARCH_KEYWORD_LENGTH } from '../../constants';
import { Suggestion } from '../../model';
import { useInputContext } from '../../providers/InputContextProvider';
import { InputWithSuggestionsTestID } from '../../test-ids';
import { SuggestionItem, SuggestionsContainer, SuggestionsWrapper } from './Suggestions.styles';

export interface SuggestionsProps {
    id: string;
    wrapperId: string;
}

export const Suggestions: FC<SuggestionsProps> = (props) => {
    const {
        isLoading,
        inputValue,
        noResultsContent,
        customQuickLinksContent,
        customSuggestionsContent,
        suggestions,
        onSelect,
        onHideSuggestions,
        focusedSuggestionIndex,
        value,
        variant,
        minKeywordLength,
    } = useInputContext();

    const containerRef = useRef<HTMLUListElement>(null);

    const handleSuggestionClick = (suggestion: Suggestion) => {
        onSelect(suggestion);
    };

    // Scroll suggestions list to top after loading new suggestions
    useEffect(() => {
        if (containerRef.current && focusedSuggestionIndex === null) {
            containerRef.current.scrollTop = 0;
        }
    }, [suggestions, focusedSuggestionIndex]);

    const minKeywordLengthValue = minKeywordLength ?? MIN_SEARCH_KEYWORD_LENGTH;

    const showNoResults =
        inputValue.length >= minKeywordLengthValue && !isLoading && !suggestions.length && inputValue !== value?.label;

    const showQuickLinks = !showNoResults && !inputValue && !suggestions.length && !!customQuickLinksContent;

    const showSuggestions = !!suggestions.length && !showNoResults && !showQuickLinks;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (KeyboardCode.Escape === e.key) {
                onHideSuggestions();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onHideSuggestions]);

    if ((showNoResults && !noResultsContent) || (!showNoResults && !suggestions.length && !showQuickLinks)) {
        return null;
    }

    return (
        <SuggestionsWrapper $variant={variant} id={props.wrapperId}>
            {showNoResults && noResultsContent}
            {showQuickLinks && customQuickLinksContent(handleSuggestionClick, props)}
            {showSuggestions &&
                customSuggestionsContent &&
                customSuggestionsContent(suggestions, handleSuggestionClick, props)}
            {showSuggestions && !customSuggestionsContent && (
                <SuggestionsContainer
                    role="combobox"
                    ref={containerRef}
                    data-test={InputWithSuggestionsTestID.SUGGESTIONS}
                >
                    {suggestions.map((suggestion, index) => (
                        <SuggestionItem
                            role="option"
                            key={suggestion.value}
                            onClick={() => handleSuggestionClick(suggestion)}
                            ref={(ref) =>
                                focusedSuggestionIndex === index &&
                                ref?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
                            }
                            {...(focusedSuggestionIndex === index && {
                                className: 'focused',
                            })}
                            data-test={InputWithSuggestionsTestID.SUGGESTION_ITEM}
                        >
                            {suggestion.label}
                        </SuggestionItem>
                    ))}
                </SuggestionsContainer>
            )}
        </SuggestionsWrapper>
    );
};
